import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    TextField,
    Typography,
    Stepper,
    Step,
    StepLabel,
    Grid,
    Card,
    CardContent,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    Collapse,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { schoolOnboarding } from '../authService';
import { useSnackbar } from '../AlertComponent/SnackbarContext';

const schema = yup.object({
    schoolName: yup.string().required("School name is required"),
    schoolAddress: yup.string().required("Address is required"),
    schoolEmail: yup
        .string()
        .email("Invalid email")
        .required("School email is required"),
    schoolPhone: yup
        .string()
        .matches(/^[0-9]{10}$/, "Phone must be 10 digits")
        .required("School phone is required"),
    schoolAffiliatedTo: yup
        .string()
        .required("Affiliated board is required"),
    // admins: yup.array().of(
    //     yup.object({
    //         name: yup.string().required("Admin name is required"),
    //         contact: yup
    //             .string()
    //             .matches(/^[0-9]{10}$/, "Phone must be 10 digits")
    //             .required("Admin phone is required"),
    //         email: yup
    //             .string()
    //             .email("Invalid email")
    //             .required("Admin email is required"),
    //     })
    // )
});

const StyledWrapper = styled(Box)({
    width: '100%',
    maxWidth: '900px',
    margin: '0 auto',
});

const StyledCard = styled(Card)({
    borderRadius: '18px',
    boxShadow: 'none',
});

const PageContainer = styled(Box)(({ active }) => ({
    display: active ? 'block' : 'none',
    animation: active ? 'fadeIn 0.5s ease' : 'none',
    '@keyframes fadeIn': {
        from: { opacity: 0, transform: 'translateY(10px)' },
        to: { opacity: 1, transform: 'translateY(0)' },
    },
}));

const SectionTitle = styled(Typography)({
    fontSize: '18px',
    fontWeight: 600,
    color: '#111827',
    marginBottom: '20px',
    borderLeft: '4px solid #6d28d9',
    paddingLeft: '12px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
});

const StyledTextField = styled(TextField)({
    '& .MuiOutlinedInput-root': {
        borderRadius: '10px',
        backgroundColor: '#ffffff',
        '& fieldset': {
            borderColor: '#d1d5db',
        },
        '&:hover fieldset': {
            borderColor: '#6d28d9',
        },
        '&.Mui-focused fieldset': {
            borderColor: '#6d28d9',
        },
    },
    '& .MuiInputBase-input': {
        fontSize: '14px',
    },
    '& textarea': {
        resize: 'vertical',
    },
});

const AddButton = styled(Button)({
    background: '#10b981',
    color: 'white',
    borderRadius: '8px',
    padding: '8px 16px',
    fontSize: '13px',
    textTransform: 'none',
    marginTop: '10px',
    '&:hover': {
        background: '#0da271',
    },
});

const RemoveButton = styled(IconButton)({
    background: '#ef4444',
    color: 'white',
    borderRadius: '6px',
    width: '30px',
    height: '30px',
    '&:hover': {
        background: '#dc2626',
    },
});

const PrimaryButton = styled(Button)({
    background: '#6d28d9',
    color: '#fff',
    border: 'none',
    padding: {
        xs: '8px 28px',
        sm: '8px 28px',
        md: '8px 28px',
        lg: '8px 28px',
        xl: '8px 28px',
    },
    borderRadius: '8px',
    fontSize: '13px',
    minHeight: '36px',
    textTransform: 'none',
    '&:hover': {
        background: '#5b21b6',
    },
});

const OutlineButton = styled(Button)({
    background: 'white',
    color: '#6d28d9',
    border: '1px solid #6d28d9',
    padding: '8px 24px',
    borderRadius: '8px',
    fontSize: '13px',
    minHeight: '36px',
    textTransform: 'none',
    '&:hover': {
        background: '#f5f3ff',
    },
});

const UploadArea = styled(Paper)({
    border: '2px dashed #9ca3af',
    borderRadius: '10px',
    padding: '30px',
    textAlign: 'center',
    cursor: 'pointer',
    transition: 'all 0.3s',
    background: 'white',
    '&:hover': {
        borderColor: '#6d28d9',
        background: '#f5f3ff',
    },
});

const SummaryBox = styled(Box)({
    background: '#f0f9ff',
    border: '1px solid #bae6fd',
    borderRadius: '10px',
    padding: '20px',
    marginBottom: '25px',
});

const ReviewItem = styled(Box)({
    background: '#f9fafb',
    padding: '15px',
    borderRadius: '8px',
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
});

const CompanyLogo = styled('img')({
    // position: 'absolute',
    // top: 0,
    // left: 0,
    maxWidth: '150px',
    // height: 'auto',
    // marginTop: '10px',
});

const StyledTableContainer = styled(TableContainer)({
    borderRadius: '12px',
    border: '1px solid #e5e7eb',
    overflow: 'hidden',
    marginBottom: '20px',
});

const StyledTableCell = styled(TableCell)({
    fontSize: '14px',
    padding: '14px 16px',
    '&.MuiTableCell-head': {
        backgroundColor: '#f9fafb',
        fontWeight: 600,
        color: '#1f2937',
        borderBottom: '1px solid #e5e7eb',
    },
    '&.MuiTableCell-body': {
        borderBottom: '1px solid #f3f4f6',
        verticalAlign: 'top',
    },
});

const documentFields = [
    { id: 'feeStructure', label: 'All Fee Structure Details (Transport/Hostel/tuition Fee)', required: true },
    { id: 'classStructure', label: 'Class Structure', required: true },
    { id: 'admissionForm', label: 'Student Admission Form', required: false },
    { id: 'marksheetSample', label: 'Marksheet Sample', required: false },
    { id: 'admitCardSample', label: 'Admit Card Sample', required: false },
    { id: 'certificateSample', label: 'Certificate-character/Transfer/Bonafide', required: false },
    { id: 'feeBookFormat', label: 'Fee Book / Receipt Format', required: false },
    { id: 'feeReportsSample', label: 'Fee Reports Sample', required: false },
    { id: 'gradingSystem', label: 'Document for explain their grading system', required: false },
    { id: 'timeTableSample', label: 'Time-Table Sample (Class, Exam)', required: false },
];

const SchoolOnboardingForm = () => {
    const [currentPage, setCurrentPage] = useState(0);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const [formData, setFormData] = useState({
        schoolName: '',
        schoolAddress: '',
        schoolState: '',
        schoolEmail: '',
        schoolPhone: '',
        schoolCountry: '',
        schoolRegNo: '',
        schoolWebsite: '',
        schoolClasses: '',
        schoolAffiliatedTo: '',
        schoolDate: new Date().toISOString().split('T')[0],
        teacherCount: 0,
        staffCount: 0,
        status: 'False'
    });

    const [directors, setDirectors] = useState([{ name: '', contact: '', email: '' }]);
    const [admins, setAdmins] = useState([{ name: '', contact: '', email: '' }]);
    const [classes, setClasses] = useState([
        { className: 'Nursery', count: 0 },
    ]);
    const { showSnackbar } = useSnackbar();
    const [sealFile, setSealFile] = useState(null);
    const [sealPreview, setSealPreview] = useState(null);
    const [teacherFile, setTeacherFile] = useState(null);
    const [feesFile, setFeesFile] = useState(null);
    const [staffFile, setStaffFile] = useState(null);
    const [submitted, setSubmitted] = useState(false);

    const [uploadedFiles, setUploadedFiles] = useState({});
    const [expandedRows, setExpandedRows] = useState({});

    const steps = ['School Details', 'Director & Admin', 'Documents Upload', 'Review & Submit'];
    const {
        register,
        trigger,
        setValue,
        formState: { errors },
    } = useForm({
        resolver: yupResolver(schema),
    });

    const handleInputChange = (e) => {
        const { name, value } = e.target;

        const onlyAlphabets = /^[A-Za-z\s]*$/;
        const onlyNumbers = /^[0-9]*$/;
        const alphaNumericWithDash = /^[A-Za-z0-9\s\-]*$/;

        let finalValue = value;

        switch (name) {
            case "schoolName":
            case "schoolState":
            case "schoolCountry":
            case "schoolAffiliatedTo":
                if (!onlyAlphabets.test(value)) return;
                break;

            case "schoolPhone":
                if (!onlyNumbers.test(value)) return;
                finalValue = value.slice(0, 10);
                break;

            case "schoolClasses":
                if (!alphaNumericWithDash.test(value)) return;
                break;

            case "teacherCount":
            case "staffCount":
                if (!onlyNumbers.test(value)) return;
                break;

            default:
                break;
        }

        setFormData((prev) => ({
            ...prev,
            [name]: finalValue,
        }));

        setValue(name, finalValue, {
            shouldValidate: finalValue !== "",
        });
    };

    const addDirector = () => {
        setDirectors(prev => [
            ...prev,
            { name: '', contact: '', email: '' }
        ]);
    };

    const removeDirector = (index) => {
        setDirectors(prev =>
            prev.length > 1
                ? prev.filter((_, i) => i !== index)
                : prev
        );
    };
    const updateDirector = (index, field, value) => {
        const newDirectors = [...directors];
        newDirectors[index][field] = value;
        setDirectors(newDirectors);
    };

    const addAdmin = () => {
        setAdmins(prev => [...prev, { name: "", contact: "", email: "" }]);
    };

    const removeAdmin = (index) => {
        if (admins.length === 1) return;
        const updated = admins.filter((_, i) => i !== index);
        setAdmins(updated);
    };

    const updateAdmin = (index, field, value) => {
        const newAdmins = [...admins];
        newAdmins[index][field] = value;
        setAdmins(newAdmins);
    };

    const addClass = () => {
        setClasses([...classes, { className: '', count: 0 }]);
    };

    const removeClass = (index) => {
        if (classes.length > 1) {
            setClasses(classes.filter((_, i) => i !== index));
        }
    };

    const updateClass = (index, field, value) => {
        const newClasses = [...classes];
        newClasses[index][field] = value;
        setClasses(newClasses);
    };

    const downloadUploadedFile = (file) => {
        if (!file) return;
        const url = URL.createObjectURL(file);
        const link = document.createElement('a');
        link.href = url;
        link.download = file.name;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const handleSealUpload = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (file.type.startsWith('image/')) {
                setSealFile(file);
                const reader = new FileReader();
                reader.onload = (event) => {
                    setSealPreview(event.target.result);
                };
                reader.readAsDataURL(file);
            }
        }
    };

    const removeSeal = () => {
        setSealFile(null);
        setSealPreview(null);
    };

    const handleDocumentUpload = (fieldId, files) => {
    if (!files || files.length === 0) return;

    const filesArray = Array.from(files);

    setUploadedFiles(prev => {
        const existingFiles = prev[fieldId] || [];
        const updatedFiles = {
            ...prev,
            [fieldId]: [...existingFiles, ...filesArray]
        };

        const totalSize = getTotalFileSize(updatedFiles);

        if (totalSize > MAX_TOTAL_SIZE) {
            showSnackbar("Total file size should not exceed 20MB", "error");
            return prev; 
        }

        return updatedFiles;
    });
};

    const removeSingleDocument = (fieldId, fileIndex) => {
        setUploadedFiles(prev => {
            const currentFiles = [...(prev[fieldId] || [])];
            currentFiles.splice(fileIndex, 1);
            if (currentFiles.length === 0) {
                const newState = { ...prev };
                delete newState[fieldId];
                return newState;
            }
            return {
                ...prev,
                [fieldId]: currentFiles
            };
        });
    };

    const toggleExpandRow = (fieldId) => {
        setExpandedRows(prev => ({
            ...prev,
            [fieldId]: !prev[fieldId]
        }));
    };

    const validatePage = async () => {
    if (currentPage === 0) {
        return await trigger([
            "schoolName",
            "schoolAddress",
            "schoolEmail",
            "schoolPhone",
            "schoolAffiliatedTo",
        ]);
    }

    if (currentPage === 1) {
        let isValid = true;
        
        for (let i = 0; i < admins.length; i++) {
            const admin = admins[i];
            
            if (admin.name === '' && admin.contact === '' && admin.email === '') {
                continue;
            }
            
            if (!admin.name || admin.name.trim() === '') {
                showSnackbar(`Admin ${i + 1}: Name is required`, "alert");
                isValid = false;
                break;
            }
            
            if (!admin.contact || admin.contact.length !== 10) {
                showSnackbar(`Admin ${i + 1}: Contact number must be 10 digits`, "alert");
                isValid = false;
                break;
            }
            
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!admin.email || !emailRegex.test(admin.email)) {
                showSnackbar(`Admin ${i + 1}: Valid email is required`, "alert");
                isValid = false;
                break;
            }
        }
        
        return isValid;
    }

    if (currentPage === 2) {
        const requiredDocs = documentFields.filter(field => field.required);
        const missingDocs = requiredDocs.filter(doc => !uploadedFiles[doc.id] || uploadedFiles[doc.id].length === 0);
        if (missingDocs.length > 0) {
            showSnackbar(`Please upload required documents: ${missingDocs.map(d => d.label).join(', ')}`, "alert");
            return false;
        }
        return true;
    }

    return true;
};

const MAX_TOTAL_SIZE = 20 * 1024 * 1024; // 20MB

const getTotalFileSize = (filesObj) => {
    let total = 0;

    Object.values(filesObj).forEach(files => {
        files.forEach(file => {
            total += file.size;
        });
    });

    return total;
};

    const handleNext = async () => {
        const isValid = await validatePage();
        if (isValid) {
            setCurrentPage((prev) => prev + 1);
        }
    };

    const handleBack = () => {
        setCurrentPage((prev) => prev - 1);
    };

    const handleSubmit = async () => {
        try {
            const formdata = new FormData();

            formdata.append("SchoolName", formData.schoolName);
            formdata.append("Address", formData.schoolAddress);
            formdata.append("State", formData.schoolState);
            formdata.append("Country", formData.schoolCountry);
            formdata.append("SchoolRegNo", formData.schoolRegNo);
            formdata.append("WebsiteUrl", formData.schoolWebsite);
            formdata.append("Classes", formData.schoolClasses);
            formdata.append("SchoolAffiliatedTo", formData.schoolAffiliatedTo);
            formdata.append("SchoolEmail", formData.schoolEmail);
            formdata.append("SchoolPhone", formData.schoolPhone);
            formdata.append("Status", formData.status);

            formdata.append(
                "DirectorName",
                directors.map(d => d.name).join(", ")
            );
            formdata.append(
                "DirectorContactNumber",
                directors.map(d => d.contact).join(", ")
            );
            formdata.append(
                "DirectorEmail",
                directors.map(d => d.email).join(", ")
            );
            formdata.append("AdminName", admins.map((a) => a.name).join(", "));

            formdata.append(
              "AdminContactNumber",
              admins.map((a) => a.contact).join(", "),
            );

            formdata.append(
              "AdminEmail",
              admins.map((a) => a.email).join(", "),
            );
            // formdata.append("StudentCount", getTotalStudents());
            // formdata.append("ClassName", classes.map(c => c.className).join(","));
            // formdata.append("TotalNumberOfTeacher", formData.teacherCount);
            // if (teacherFile) formdata.append("TeacherAttachFile", teacherFile);
            // formdata.append("TotalBackOfficeStaff", formData.staffCount);
            // if (staffFile) formdata.append("TotalBackOfficeAttachFile", staffFile);
            if (sealFile) formdata.append("SchoolSignUpload", sealFile);

            const documentMapping = {
    feeStructure: "FeeStructureFile",
    classStructure: "ClassStructureFile",
    admissionForm: "StudentAdmissionFormFile",
    marksheetSample: "MarksheetSampleFile",
    admitCardSample: "AdmitCardSampleFile",
    certificateSample: "CertificateSampleFile",
    feeBookFormat: "FeesbookReciptSampleFile",
    feeReportsSample: "FeesReportSampleFile",
    gradingSystem: "DocumentgradingSystemSamplefile",
    timeTableSample: "TimetableSampleFile"
};

          for (const [key, files] of Object.entries(uploadedFiles)) {
    const dbField = documentMapping[key];

    if (dbField && files?.length > 0) {
        files.forEach((file) => {
            formdata.append(dbField, file);
        });
    }
}
            const response = await schoolOnboarding(formdata);

            if (response?.success) {
                showSnackbar(response.message || "Form submitted successfully", "success");
                setSubmitted(true);
            } else {
                showSnackbar(response.message || "Submission failed", "alert");
            }
        } catch (error) {
            showSnackbar(
                error?.response?.data?.message || "Something went wrong",
                "error"
            );
        }
    };

    const getTotalStudents = () => {
        return classes.reduce((sum, cls) => sum + (parseInt(cls.count) || 0), 0);
    };

    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    if (submitted) {
        return (
            <Box
                sx={{
                    minHeight: '100vh',
                    background: 'linear-gradient(135deg, #fff6e5, #eef2ff)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    padding: '20px',
                }}
            >
                <StyledWrapper>
                    <StyledCard>
                        <Box sx={{ textAlign: 'center', padding: '40px 20px' }}>
                            <Typography sx={{ fontSize: '48px', color: '#10b981', marginBottom: '20px' }}>
                                ✓
                            </Typography>
                            <Typography variant="h5" sx={{ color: '#111827', marginBottom: '15px' }}>
                                Form Submitted Successfully!
                            </Typography>
                            <Typography sx={{ color: '#6b7280', marginBottom: '30px' }}>
                                Your school onboarding form has been submitted. We will contact you shortly.
                            </Typography>
                            <PrimaryButton onClick={() => window.location.reload()}>
                                Submit Another Form
                            </PrimaryButton>
                        </Box>
                    </StyledCard>
                </StyledWrapper>
            </Box>
        );
    }

    return (
        <Box
            sx={{
                background: 'linear-gradient(135deg, #fff6e5, #eef2ff)',
                // display: 'flex',
                justifyContent: 'center',
                padding: {
                    xs: '20px',
                    sm: 0,
                    md: 0,
                },
                position: 'relative',
            }}
        >
        <Box sx={{
                      padding: {
                    xs: '5px 20px',
                    sm: '10px 40px',
                    md: '20px 70px',
                },
                display:'flex',
                justifyContent:'space-between',
                boxShadow: '0 0 10px 0 #0000001a',
    background: '#fffaf0',
    alignItems: 'center',
                position: 'relative',
        }}>
            <CompanyLogo
                src="assets/brandlogo.png"
                alt="Company Logo"
            />
            <a href="/https://violetsync.com" target="_blank" style={{ color: "#6d28d9",
            fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif', }}>Visit Website</a>
</Box>
            <StyledWrapper>
                <StyledCard
                    sx={{
                        padding: {
                            xs: '40px 0 20px 0',
                            sm: '20px 24px',
                            md: '25px 35px',
                            lg: '30px 45px',
                            xl: '40px 60px',
                        },
                    }}
                >
                    <Typography variant="h4" sx={{ textAlign: 'center', fontWeight: 700, fontSize: '28px', margin: 0, color: '#1f2937', paddingTop: { sm: '20px' } }}>
                        ON BOARDING SCHOOL FORM
                    </Typography>
                    <Typography sx={{ textAlign: 'center', margin: '6px 0 35px', color: '#6b7280' }}>
                        Complete all sections to submit your school information
                    </Typography>
                    <div style={{ overflowX: "auto", paddingBottom: "10px" }}>
                    <Stepper activeStep={currentPage} alternativeLabel sx={{ marginBottom: '45px' , minWidth: isMobile ? "500px" : "auto",}}>
                        {steps.map((label, index) => (
                            <Step key={label}>
                                <StepLabel
                                    sx={{
                                        '& .MuiStepLabel-label': {
                                            fontSize: '14px',
                                            color: currentPage === index ? '#6d28d9' : '#9ca3af',
                                            fontWeight: currentPage === index ? 600 : 400,
                                            marginTop: isMobile ? "4px" : "8px",
                                        },
                                        '& .MuiStepIcon-root': {
                                            fontSize: isMobile ? "20px" : "32px",
                                            color: currentPage === index ? '#6d28d9' : '#d1d5db',
                                            '&.Mui-active': {
                                                color: '#6d28d9',
                                            },
                                        },
                                    }}
                                >
                                    {label}
                                </StepLabel>
                            </Step>
                        ))}
                    </Stepper>
</div>
                    {/* Page 1: School Details */}
                    <PageContainer active={currentPage === 0}>
                        <SectionTitle>School Details</SectionTitle>
                        <Grid container spacing={2}>
                            <Grid item size={{ xs: 12 }}>
                                <Typography sx={{ fontSize: '13px', color: '#374151', marginBottom: '6px', fontWeight: 500 }}>
                                    School Name
                                </Typography>
                                <StyledTextField
                                    fullWidth
                                    {...register("schoolName")}
                                    name="schoolName"
                                    placeholder="Enter School Name"
                                    value={formData.schoolName}
                                    onChange={handleInputChange}
                                    error={!!errors.schoolName}
                                />
                            </Grid>
                            <Grid item size={{ xs: 12 }}>
                                <Typography sx={{ fontSize: '13px', color: '#374151', marginBottom: '6px', fontWeight: 500 }}>
                                    Address
                                </Typography>
                                <StyledTextField
                                    fullWidth
                                    multiline
                                    rows={3}
                                    {...register("schoolAddress")}
                                    name="schoolAddress"
                                    placeholder="Enter School Address"
                                    value={formData.schoolAddress}
                                    onChange={handleInputChange}
                                    error={!!errors.schoolAddress}
                                />
                            </Grid>
                            <Grid item size={{ xs: 12, md: 6 }}>
                                <Typography sx={{ fontSize: '13px', color: '#374151', marginBottom: '6px', fontWeight: 500 }}>
                                    State
                                </Typography>
                                <StyledTextField
                                    fullWidth
                                    name="schoolState"
                                    placeholder="Enter State Name"
                                    value={formData.schoolState}
                                    onChange={handleInputChange}
                                    variant="outlined"
                                />
                            </Grid>
                            <Grid item size={{ xs: 12, md: 6 }}>
                                <Typography sx={{ fontSize: '13px', color: '#374151', marginBottom: '6px', fontWeight: 500 }}>
                                    Country
                                </Typography>
                                <StyledTextField
                                    fullWidth
                                    name="schoolCountry"
                                    placeholder="Enter Country Name"
                                    value={formData.schoolCountry}
                                    onChange={handleInputChange}
                                    variant="outlined"
                                />
                            </Grid>
                            <Grid item size={{ xs: 12, md: 6 }}>
                                <Typography sx={{ fontSize: '13px', color: '#374151', marginBottom: '6px', fontWeight: 500 }}>
                                    School Reg. No.
                                </Typography>
                                <StyledTextField
                                    fullWidth
                                    name="schoolRegNo"
                                    placeholder="Enter School Registration Number"
                                    value={formData.schoolRegNo}
                                    onChange={handleInputChange}
                                    variant="outlined"
                                />
                            </Grid>
                            <Grid item size={{ xs: 12, md: 6 }}>
                                <Typography sx={{ fontSize: '13px', color: '#374151', marginBottom: '6px', fontWeight: 500 }}>
                                    Website
                                </Typography>
                                <StyledTextField
                                    fullWidth
                                    name="schoolWebsite"
                                    placeholder="Enter Website Name"
                                    value={formData.schoolWebsite}
                                    onChange={handleInputChange}
                                    type="url"
                                    variant="outlined"
                                />
                            </Grid>
                            <Grid item size={{ xs: 12, md: 6 }}>
                                <Typography sx={{ fontSize: '13px', color: '#374151', marginBottom: '6px', fontWeight: 500 }}>
                                    Classes
                                </Typography>
                                <StyledTextField
                                    fullWidth
                                    name="schoolClasses"
                                    value={formData.schoolClasses}
                                    onChange={handleInputChange}
                                    placeholder="e.g., Nursery to Class 10"
                                    variant="outlined"
                                />
                            </Grid>
                            <Grid item size={{ xs: 12, md: 6 }}>
                                <Typography sx={{ fontSize: '13px', color: '#374151', marginBottom: '6px', fontWeight: 500 }}>
                                    Affiliated To
                                </Typography>
                                <StyledTextField
                                    fullWidth
                                    {...register("schoolAffiliatedTo")}
                                    name="schoolAffiliatedTo"
                                    value={formData.schoolAffiliatedTo}
                                    onChange={handleInputChange}
                                    error={!!errors.schoolAffiliatedTo}
                                    placeholder="e.g., CBSE / ICSE / State Board"
                                    variant="outlined"
                                />
                            </Grid>
                            <Grid item size={{ xs: 12, md: 6 }}>
                                <Typography sx={{ fontSize: '13px', color: '#374151', mb: '6px', fontWeight: 500 }}>
                                    School Email
                                </Typography>
                                <StyledTextField
                                    fullWidth
                                    {...register("schoolEmail")}
                                    name="schoolEmail"
                                    value={formData.schoolEmail}
                                    onChange={handleInputChange}
                                    error={!!errors.schoolEmail}
                                    placeholder="school@example.com"
                                    type="email"
                                />
                            </Grid>
                            <Grid item size={{ xs: 12, md: 6 }}>
                                <Typography sx={{ fontSize: '13px', color: '#374151', mb: '6px', fontWeight: 500 }}>
                                    Phone Number
                                </Typography>
                                <StyledTextField
                                    fullWidth
                                    {...register("schoolPhone")}
                                    name="schoolPhone"
                                    value={formData.schoolPhone}
                                    onChange={handleInputChange}
                                    error={!!errors.schoolPhone}
                                    placeholder="10 digit mobile number"
                                    type="tel"
                                />
                            </Grid>
                        </Grid>

                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', marginTop: '45px', paddingTop: '25px', borderTop: '1px solid #e5e7eb' }}>
                            <PrimaryButton onClick={handleNext}>Next</PrimaryButton>
                        </Box>
                    </PageContainer>

                    {/* Page 2: Director & Admin */}
                    <PageContainer active={currentPage === 1}>
                        <SectionTitle>Director Details</SectionTitle>
                        <Box sx={{ marginBottom: '25px' }}>
                            {directors.map((director, index) => (
                                <Grid container key={index} spacing={2} sx={{ marginBottom: '15px', alignItems: 'center' }}>
                                    <Grid item size={{ xs: 12, md: 5.5, sm: 5.5 }}>
                                        <StyledTextField
                                            fullWidth
                                            placeholder="Director Name"
                                            value={director.name}
                                            onChange={(e) => {
                                                const value = e.target.value;
                                                if (!/^[A-Za-z\s]*$/.test(value)) return;
                                                updateDirector(index, 'name', value);
                                            }}
                                            variant="outlined"
                                        />
                                    </Grid>
                                    <Grid item size={{ xs: 12, md: 5.5, sm: 5.5 }}>
                                        <StyledTextField
                                            fullWidth
                                            placeholder="Contact Number"
                                            value={director.contact}
                                            onChange={(e) => {
                                                const value = e.target.value;
                                                if (!/^[0-9]*$/.test(value)) return;
                                                updateDirector(index, 'contact', value.slice(0, 10));
                                            }}
                                            variant="outlined"
                                        />
                                    </Grid>
                                    <Grid item size={{ xs: 12, md: 5.5, sm: 5.5 }}>
                                        <StyledTextField
                                            fullWidth
                                            placeholder="Director Email"
                                            type="email"
                                            value={director.email}
                                            onChange={(e) => {
                                                const newDirectors = [...directors];
                                                newDirectors[index].email = e.target.value;
                                                setDirectors(newDirectors);
                                            }}
                                            variant="outlined"
                                        />
                                    </Grid>
                                    <Grid item size={{ xs: 12, md: 1 }}>
                                        <RemoveButton onClick={() => removeDirector(index)}>
                                            <CloseIcon sx={{ fontSize: '18px' }} />
                                        </RemoveButton>
                                    </Grid>
                                </Grid>
                            ))}
                            <AddButton startIcon={<AddIcon />} onClick={addDirector}>
                                Add Director
                            </AddButton>
                        </Box>


                        <SectionTitle>Admin Details</SectionTitle>
                        <Box sx={{ marginBottom: '25px' }}>
                            {admins.map((admin, index) => (
                                <Grid container key={index} spacing={2} sx={{ marginBottom: '15px', alignItems: 'center' }}>
                                    <Grid item size={{ xs: 12, md: 5.5, sm: 5.5 }}>
                                        <StyledTextField
                                            fullWidth
                                            placeholder="Admin Name"
                                            name={`admins.${index}.name`}
                                            value={admin.name}
                                            error={!!errors?.admins?.[index]?.name}
                                            helperText={errors?.admins?.[index]?.name?.message}
                                            onChange={(e) => {
                                                const value = e.target.value;
                                                if (!/^[A-Za-z\s]*$/.test(value)) return;
                                                updateAdmin(index, 'name', value);
                                                setValue(`admins.${index}.name`, value, {
                                                    shouldValidate: true,
                                                    shouldDirty: true
                                                });
                                            }}
                                            variant="outlined"
                                        />
                                    </Grid>
                                    <Grid item size={{ xs: 12, md: 5.5, sm: 5.5 }}>
                                        <StyledTextField
                                            fullWidth
                                            placeholder="Contact Number"
                                            name={`admins.${index}.contact`}
                                            value={admin.contact}
                                            error={!!errors?.admins?.[index]?.contact}
                                            helperText={errors?.admins?.[index]?.contact?.message}
                                            onChange={(e) => {
                                                let value = e.target.value;
                                                if (!/^[0-9]*$/.test(value)) return;
                                                value = value.slice(0, 10);
                                                updateAdmin(index, 'contact', value);
                                                setValue(`admins.${index}.contact`, value, {
                                                    shouldValidate: true,
                                                    shouldDirty: true,
                                                });
                                            }}
                                            variant="outlined"
                                        />
                                    </Grid>
                                    <Grid item size={{ xs: 12, md: 5.5, sm: 5.5 }}>
                                        <StyledTextField
                                            fullWidth
                                            placeholder="Admin Email"
                                            type="email"
                                            name={`admins.${index}.email`}
                                            value={admin.email}
                                            error={!!errors?.admins?.[index]?.email}
                                            helperText={errors?.admins?.[index]?.email?.message}
                                            onChange={(e) => {
                                                const value = e.target.value;
                                                updateAdmin(index, 'email', value);
                                                setValue(`admins.${index}.email`, value, {
                                                    shouldValidate: true,
                                                    shouldDirty: true
                                                });
                                            }}
                                            variant="outlined"
                                        />
                                    </Grid>
                                    <Grid item size={{ xs: 12, md: 1 }}>
                                        <RemoveButton onClick={() => removeAdmin(index)}>
                                            <CloseIcon sx={{ fontSize: '18px' }} />
                                        </RemoveButton>
                                    </Grid>
                                </Grid>
                            ))}
                            <AddButton startIcon={<AddIcon />} onClick={addAdmin}>
                                Add Admin
                            </AddButton>
                        </Box>

                        <Box sx={{ marginTop: '30px', padding: '20px', background: '#f9fafb', borderRadius: '12px', border: '1px solid #e5e7eb' }}>
                            <SectionTitle>School Sign and Seal</SectionTitle>
                            <input
                                type="file"
                                id="sealFile"
                                accept="image/*,.pdf"
                                style={{ display: 'none' }}
                                onChange={handleSealUpload}
                            />
                            {!sealPreview ? (
                                <UploadArea onClick={() => document.getElementById('sealFile').click()}>
                                    <Typography sx={{ fontSize: '48px', color: '#6b7280', marginBottom: '15px' }}>
                                        🖼️
                                    </Typography>
                                    <Typography sx={{ fontSize: '14px', color: '#374151', marginBottom: '5px' }}>
                                        Click to upload School Sign and Seal
                                    </Typography>
                                    <Typography sx={{ fontSize: '12px', color: '#6b7280' }}>
                                        PNG, JPG, PDF up to 5MB
                                    </Typography>
                                </UploadArea>
                            ) : (
                                <Box sx={{ marginTop: '20px', textAlign: 'center' }}>
                                    <Typography sx={{ fontSize: '14px', color: '#374151', marginBottom: '10px' }}>
                                        Preview:
                                    </Typography>
                                    <img
                                        src={sealPreview}
                                        alt="Seal Preview"
                                        style={{
                                            maxWidth: '200px',
                                            maxHeight: '150px',
                                            borderRadius: '8px',
                                            border: '1px solid #e5e7eb',
                                        }}
                                    />
                                    <Box sx={{ marginTop: '10px' }}>
                                        <OutlineButton
                                            sx={{ padding: '6px 12px', fontSize: '12px' }}
                                            onClick={removeSeal}
                                        >
                                            Remove
                                        </OutlineButton>
                                    </Box>
                                </Box>
                            )}
                        </Box>

                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: '15px', marginTop: '45px', paddingTop: '25px', borderTop: '1px solid #e5e7eb' }}>
                            <OutlineButton onClick={handleBack}>Previous</OutlineButton>
                            <PrimaryButton onClick={handleNext}>Next</PrimaryButton>
                        </Box>
                    </PageContainer>

                    {/* Page 3: Documents Upload (Multiple File Support) */}
                    <PageContainer active={currentPage === 2}>
                        <SectionTitle>Document Uploads</SectionTitle>

                        <StyledTableContainer component={Paper} elevation={0}>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <StyledTableCell sx={{ width: '40%' }}>Document Name</StyledTableCell>
                                        <StyledTableCell sx={{ width: '15%' }}>Requirement</StyledTableCell>
                                        <StyledTableCell sx={{ width: '45%' }}>Uploaded Files</StyledTableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {documentFields.map((field) => {
                                        const files = uploadedFiles[field.id] || [];
                                        const isExpanded = expandedRows[field.id];
                                        const hasFiles = files.length > 0;
                                        const displayFiles = isExpanded ? files : files.slice(0, 2);
                                        const hasMore = files.length > 2;

                                        return (
                                            <TableRow key={field.id}>
                                                <StyledTableCell>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                        {hasFiles && (
                                                            <InsertDriveFileIcon sx={{ fontSize: '20px', color: '#6d28d9' }} />
                                                        )}
                                                        <Typography sx={{ fontSize: '14px', fontWeight: field.required ? 500 : 400 }}>
                                                            {field.label}
                                                            {field.required && (
                                                                <span style={{ color: '#ef4444', marginLeft: '4px' }}>*</span>
                                                            )}
                                                        </Typography>
                                                    </Box>
                                                </StyledTableCell>
                                                <StyledTableCell>
                                                    <Chip
                                                        label={field.required ? "Required" : "Optional"}
                                                        size="small"
                                                        sx={{
                                                            backgroundColor: field.required ? '#fee2e2' : '#e0e7ff',
                                                            color: field.required ? '#dc2626' : '#4f46e5',
                                                            fontSize: '12px',
                                                            fontWeight: 500,
                                                        }}
                                                    />
                                                </StyledTableCell>
                                                <StyledTableCell>
                                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                                        <Button
                                                            component="label"
                                                            variant="outlined"
                                                            size="small"
                                                            startIcon={<CloudUploadIcon />}
                                                            sx={{
                                                                textTransform: 'none',
                                                                borderColor: '#d1d5db',
                                                                color: '#4b5563',
                                                                alignSelf: 'flex-start',
                                                                '&:hover': {
                                                                    borderColor: '#6d28d9',
                                                                    backgroundColor: '#f5f3ff',
                                                                },
                                                            }}
                                                        >
                                                            Upload Files (Multiple)
                                                            <input
                                                                type="file"
                                                                hidden
                                                                multiple
                                                                accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
                                                                onChange={(e) => {
                                                                    if (e.target.files && e.target.files.length > 0) {
                                                                        handleDocumentUpload(field.id, e.target.files);
                                                                        e.target.value = '';
                                                                    }
                                                                }}
                                                            />
                                                        </Button>

                                                        {hasFiles && (
                                                            <Box sx={{ mt: 1 }}>
                                                                <List dense disablePadding>
                                                                    {displayFiles.map((file, idx) => (
                                                                        <ListItem key={idx} disableGutters sx={{ py: 0.5, px: 0 }}>
                                                                            <ListItemText
                                                                                primary={
                                                                                    <Typography
                                                                                        sx={{
                                                                                            fontSize: '13px',
                                                                                            cursor: 'pointer',
                                                                                            color: '#2563eb',
                                                                                            '&:hover': { textDecoration: 'underline' }
                                                                                        }}
                                                                                    >
                                                                                        {file.name}
                                                                                    </Typography>
                                                                                }
                                                                                secondary={formatFileSize(file.size)}
                                                                                secondaryTypographyProps={{ fontSize: '11px' }}
                                                                            />
                                                                            <ListItemSecondaryAction>
                                                                                <IconButton
                                                                                    edge="end"
                                                                                    size="small"
                                                                                    onClick={() => removeSingleDocument(field.id, idx)}
                                                                                    sx={{ color: '#ef4444' }}
                                                                                >
                                                                                    <DeleteIcon fontSize="small" />
                                                                                </IconButton>
                                                                            </ListItemSecondaryAction>
                                                                        </ListItem>
                                                                    ))}
                                                                </List>
                                                                {hasMore && (
                                                                    <Button
                                                                        size="small"
                                                                        onClick={() => toggleExpandRow(field.id)}
                                                                        endIcon={isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                                                        sx={{ textTransform: 'none', fontSize: '12px', mt: 0.5 }}
                                                                    >
                                                                        {isExpanded ? 'Show Less' : `Show ${files.length - 2} more file(s)`}
                                                                    </Button>
                                                                )}

                                                            </Box>
                                                        )}
                                                    </Box>
                                                </StyledTableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </StyledTableContainer>

                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: '15px', marginTop: '45px', paddingTop: '25px', borderTop: '1px solid #e5e7eb' }}>
                            <OutlineButton onClick={handleBack}>Previous</OutlineButton>
                            <PrimaryButton onClick={handleNext}>Next</PrimaryButton>
                        </Box>
                    </PageContainer>

                    {/* Page 4: Review & Submit */}
                    <PageContainer active={currentPage === 3}>
                        {/* <SectionTitle>Review and Submit</SectionTitle>

                        <SummaryBox>
                            <Typography variant="h6" sx={{ marginTop: 0, color: '#0369a1' }}>
                                Submission Summary
                            </Typography>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px', fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif' }}>
                                <span>Total Students:</span>
                                <span>{getTotalStudents()}</span>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px', fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif' }}>
                                <span>Total Teachers:</span>
                                <span>{formData.teacherCount}</span>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px', fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif' }}>
                                <span>Total Staff:</span>
                                <span>{formData.staffCount}</span>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px', fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif' }}>
                                <span>Directors Added:</span>
                                <span>{directors.length}</span>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif' }}>
                                <span>Admins Added:</span>
                                <span>{admins.length}</span>
                            </Box>
                        </SummaryBox> */}

                        <Box sx={{ marginBottom: '30px' }}>
                            <SectionTitle>School Details</SectionTitle>
                            <Grid container spacing={2}>
                                <Grid item size={{ xs: 12, md: 4 }}>
                                    <ReviewItem>
                                        <Typography sx={{ fontSize: '12px', color: '#6b7280', marginBottom: '5px' }}>
                                            School Name
                                        </Typography>
                                        <Typography sx={{ fontSize: '14px', fontWeight: 500, color: '#111827' }}>
                                            {formData.schoolName || '-'}
                                        </Typography>
                                    </ReviewItem>
                                </Grid>
                                <Grid item size={{ xs: 12, md: 4 }}>
                                    <ReviewItem>
                                        <Typography sx={{ fontSize: '12px', color: '#6b7280', marginBottom: '5px' }}>
                                            Address
                                        </Typography>
                                        <Typography sx={{ fontSize: '14px', fontWeight: 500, color: '#111827' }}>
                                            {formData.schoolAddress || '-'}
                                        </Typography>
                                    </ReviewItem>
                                </Grid>
                                <Grid item size={{ xs: 12, md: 4 }}>
                                    <ReviewItem>
                                        <Typography sx={{ fontSize: '12px', color: '#6b7280', marginBottom: '5px' }}>
                                            State & Country
                                        </Typography>
                                        <Typography sx={{ fontSize: '14px', fontWeight: 500, color: '#111827' }}>
                                            {formData.schoolState && formData.schoolCountry
                                                ? `${formData.schoolState}, ${formData.schoolCountry}`
                                                : '-'}
                                        </Typography>
                                    </ReviewItem>
                                </Grid>
                                <Grid item size={{ xs: 12, md: 4 }}>
                                    <ReviewItem>
                                        <Typography sx={{ fontSize: '12px', color: '#6b7280', marginBottom: '5px' }}>
                                            Registration No.
                                        </Typography>
                                        <Typography sx={{ fontSize: '14px', fontWeight: 500, color: '#111827' }}>
                                            {formData.schoolRegNo || '-'}
                                        </Typography>
                                    </ReviewItem>
                                </Grid>
                                <Grid item size={{ xs: 12, md: 4 }}>
                                    <ReviewItem>
                                        <Typography sx={{ fontSize: '12px', color: '#6b7280', marginBottom: '5px' }}>
                                            Website
                                        </Typography>
                                        <Typography sx={{ fontSize: '14px', fontWeight: 500, color: '#111827' }}>
                                            {formData.schoolWebsite || '-'}
                                        </Typography>
                                    </ReviewItem>
                                </Grid>
                                <Grid item size={{ xs: 12, md: 4 }}>
                                    <ReviewItem>
                                        <Typography sx={{ fontSize: '12px', color: '#6b7280', marginBottom: '5px' }}>
                                            Classes
                                        </Typography>
                                        <Typography sx={{ fontSize: '14px', fontWeight: 500, color: '#111827' }}>
                                            {formData.schoolClasses || '-'}
                                        </Typography>
                                    </ReviewItem>
                                </Grid>
                                <Grid item size={{ xs: 12, md: 4 }}>
                                    <ReviewItem>
                                        <Typography sx={{ fontSize: '12px', color: '#6b7280' }}>
                                            School Email
                                        </Typography>
                                        <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>
                                            {formData.schoolEmail || '-'}
                                        </Typography>
                                    </ReviewItem>
                                </Grid>
                                <Grid item size={{ xs: 12, md: 4 }}>
                                    <ReviewItem>
                                        <Typography sx={{ fontSize: '12px', color: '#6b7280' }}>
                                            School Phone
                                        </Typography>
                                        <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>
                                            {formData.schoolPhone || '-'}
                                        </Typography>
                                    </ReviewItem>
                                </Grid>
                                <Grid item size={{ xs: 12, md: 4 }}>
                                    <ReviewItem>
                                        <Typography sx={{ fontSize: '12px', color: '#6b7280' }}>
                                            Affiliated To
                                        </Typography>
                                        <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>
                                            {formData.schoolAffiliatedTo || '-'}
                                        </Typography>
                                    </ReviewItem>
                                </Grid>
                            </Grid>
                        </Box>

                        <Box sx={{ marginBottom: '30px' }}>
                            <SectionTitle>Director Details</SectionTitle>
                            <Grid container spacing={2}>
                                {directors.map((director, index) => (
                                    <React.Fragment key={index}>
                                        <Grid item size={{ xs: 12, md: 4 }}>
                                            <ReviewItem>
                                                <Typography sx={{ fontSize: '12px', color: '#6b7280' }}>
                                                    Director Name
                                                </Typography>
                                                <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>
                                                    {director.name || '-'}
                                                </Typography>
                                            </ReviewItem>
                                        </Grid>
                                        <Grid item size={{ xs: 12, md: 4 }}>
                                            <ReviewItem>
                                                <Typography sx={{ fontSize: '12px', color: '#6b7280' }}>
                                                    Director Phone
                                                </Typography>
                                                <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>
                                                    {director.contact || '-'}
                                                </Typography>
                                            </ReviewItem>
                                        </Grid>
                                        <Grid item size={{ xs: 12, md: 4 }}>
                                            <ReviewItem>
                                                <Typography sx={{ fontSize: '12px', color: '#6b7280' }}>
                                                    Director Email
                                                </Typography>
                                                <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>
                                                    {director.email || '-'}
                                                </Typography>
                                            </ReviewItem>
                                        </Grid>
                                    </React.Fragment>
                                ))}
                            </Grid>
                        </Box>

                        <Box sx={{ marginBottom: '30px' }}>
                            <SectionTitle>Admin Details</SectionTitle>
                            <Grid container spacing={2}>
                                {admins.map((admin, index) => (
                                    <React.Fragment key={index}>
                                        <Grid item size={{ xs: 12, md: 4 }}>
                                            <ReviewItem>
                                                <Typography sx={{ fontSize: '12px', color: '#6b7280' }}>
                                                    Admin Name
                                                </Typography>
                                                <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>
                                                    {admin.name || '-'}
                                                </Typography>
                                            </ReviewItem>
                                        </Grid>
                                        <Grid item size={{ xs: 12, md: 4 }}>
                                            <ReviewItem>
                                                <Typography sx={{ fontSize: '12px', color: '#6b7280' }}>
                                                    Admin Phone
                                                </Typography>
                                                <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>
                                                    {admin.contact || '-'}
                                                </Typography>
                                            </ReviewItem>
                                        </Grid>
                                        <Grid item size={{ xs: 12, md: 4 }}>
                                            <ReviewItem>
                                                <Typography sx={{ fontSize: '12px', color: '#6b7280' }}>
                                                    Admin Email
                                                </Typography>
                                                <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>
                                                    {admin.email || '-'}
                                                </Typography>
                                            </ReviewItem>
                                        </Grid>
                                    </React.Fragment>
                                ))}
                            </Grid>
                        </Box>

                        <Box sx={{ marginBottom: '30px' }}>
                            <SectionTitle>Uploaded Documents</SectionTitle>
                            <Grid container spacing={2}>
                                {documentFields.map((field) => {
                                    const files = uploadedFiles[field.id] || [];
                                    return (
                                        <Grid item size={{ xs: 12, md: 6 }} key={field.id}>
                                            <ReviewItem>
                                                <Typography sx={{ fontSize: '12px', color: '#6b7280', marginBottom: '8px' }}>
                                                    {field.label} {field.required && '(Required)'}
                                                </Typography>
                                                {files.length > 0 ? (
                                                    <Box>
                                                        {files.map((file, idx) => (
                                                            <Typography key={idx} sx={{ fontSize: '13px', fontWeight: 500, color: '#111827', mb: 0.5 }}>
                                                                • {file.name}
                                                            </Typography>
                                                        ))}

                                                    </Box>
                                                ) : (
                                                    <Typography sx={{ fontSize: '14px', fontWeight: 500, color: '#9ca3af' }}>
                                                        Not uploaded
                                                    </Typography>
                                                )}
                                            </ReviewItem>
                                        </Grid>
                                    );
                                })}
                            </Grid>
                        </Box>

                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: '15px', marginTop: '45px', paddingTop: '25px', borderTop: '1px solid #e5e7eb' }}>
                            <OutlineButton onClick={handleBack}>Previous</OutlineButton>
                            <PrimaryButton onClick={handleSubmit}>Submit Form</PrimaryButton>
                        </Box>
                    </PageContainer>
                </StyledCard>
            </StyledWrapper>
            <Box
            sx={{
                border:'1px solid #e5e7eb',
                padding:'10px 0 20px 0',
                textAlign:'center',
            }}
            >
            <img src="assets/logom.png" style={{width:300}}/>
            <Box as={'p'}
            sx={{
                display: 'flex',
                padding:'0 15px',
                justifyContent: 'center',
                fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif', 
            }}
            >© 2025 MOUNT SAFFRON TECHNOLOGIES AND CONSULTANT PRIVATE LIMITED. All rights reserved.</Box>
            <Box
            sx={{
                display: 'flex',
                justifyContent: 'center',
                fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif', 
                padding:'15px',
                gap:'50px',
                flexWrap:'wrap'
            }}>
                <Box as={'span'}>GSTIN: - 08AARCM4717G1Z5</Box>
                <Box as={'span'}>CIN: U62091RJ2023PTC091054</Box>
            </Box>
                <Box as={'span'}>152, Tonk Rd, Opposite Jaipur Hospital
Jai Ambey Nagar,<br/> Gopal Pura Mode
Jaipur, Rajasthan – 302018</Box>
            </Box>
        </Box>
    );
};

export default SchoolOnboardingForm;